// ─────────────────────────────────────────────────────────────────────────
// Sabril · Audit resto — Shared Design System (single source of truth)
// Editorial Mediterranean brand identity — warm ivory, deep forest green,
// gold accent. Used across every surface: pages, modals, forms, PDF.
// ─────────────────────────────────────────────────────────────────────────

export const DA = {
  // Backgrounds
  ivory: '#F7EEDF',          // page background (warm cream)
  ivoryDeep: '#F1E4CE',      // slightly darker for cards/sections
  ivoryLight: '#FBF5EA',     // lightest, for pills, inputs, hover states
  paper: '#FFFDF7',          // near-white paper for high-contrast forms

  // Deep forest greens — primary title + brand voice
  greenDeep: '#0B2B24',
  greenMid: '#123D33',
  greenSoft: 'rgba(11,43,36,0.10)',

  // Gold accent — signature color
  goldMain: '#D89440',
  goldDeep: '#B8792F',
  goldPale: '#EED7B2',
  goldSoft: 'rgba(216,148,64,0.22)',   // hairlines/borders
  goldSofter: 'rgba(216,148,64,0.10)', // sublte fill

  // Vibrant sun orange — for standalone iconic sun ornaments
  sunBright: '#E87C1F',
  sunBrightDeep: '#C4661A',

  // Ink (body text on ivory)
  ink: '#3C3A34',
  inkMuted: '#5A5652',
  inkFaint: '#8A8580',

  // Legacy palette compat (for cards that still need color tags)
  wallet: {
    emerald: { from: '#186E56', to: '#0B4432', accent: '#5FB893', label: 'MATIÈRE' },
    gold:    { from: '#B18A4B', to: '#7A5C25', accent: '#E9C58A', label: 'REVENUS' },
    indigo:  { from: '#3F4A9E', to: '#1D2665', accent: '#8B95E6', label: 'ÉQUIPE' },
    violet:  { from: '#6E40BF', to: '#3B1F7C', accent: '#B49DE6', label: 'STABILITÉ' },
    coral:   { from: '#C74A44', to: '#7F2A25', accent: '#F39690', label: 'FIDÉLITÉ' },
    graphite:{ from: '#2A2A2D', to: '#0F0F11', accent: '#6E6E73', label: 'IDENTITÉ' },
    cyan:    { from: '#1E88B5', to: '#0D5374', accent: '#7DC9E6', label: 'DIGITAL' },
    slate:   { from: '#4F5A6C', to: '#252D3B', accent: '#8995A8', label: 'DÉTAIL' },
  },

  // Typography
  serif: '"Playfair Display", Georgia, serif',
  sans:  '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Inter", "Segoe UI", Roboto, sans-serif',
  mono:  '"Space Mono", "SF Mono", ui-monospace, Menlo, monospace',

  // Radii
  radiusSm: 8,
  radiusMd: 14,
  radiusLg: 20,
  radiusXl: 28,

  // Motion
  easeRefined: [0.32, 0.72, 0, 1],
  transition: '320ms cubic-bezier(0.32, 0.72, 0, 1)',
};

// ─── Sun ornament — REAL PHOTOREALISTIC PNG asset ──────────────────────
export const SunOrnament = ({ size = 22, color, opacity = 1 }) => (
  <img
    src="/da/sun-ornament.png"
    alt=""
    aria-hidden
    className="inline-block pointer-events-none select-none"
    style={{
      width: size,
      height: size,
      opacity,
      // If a custom color is passed, hint the color via CSS filter (light tint)
      ...(color && color !== '#D89440' && color !== '#E87C1F' ? {
        filter: `drop-shadow(0 0 0 ${color})`,
      } : {}),
    }}
  />
);

// ─── Awning stripe corner — REAL PHOTOREALISTIC PNG asset ─────────────
// Uses AI-generated draped fabric asset from /public/da/
export const AwningCorner = ({ position = 'top-right', size = 180, opacity = 1 }) => {
  // Each corner has its own optimal image + rotation combo
  const config = {
    'top-left':     { src: '/da/awning-corner-tl.png', style: { top: -size * 0.28, left: -size * 0.20 }, transform: 'none' },
    'top-right':    { src: '/da/awning-corner-tl.png', style: { top: -size * 0.28, right: -size * 0.20 }, transform: 'scaleX(-1)' },
    'bottom-right': { src: '/da/awning-corner-br.png', style: { bottom: -size * 0.28, right: -size * 0.20 }, transform: 'none' },
    'bottom-left':  { src: '/da/awning-corner-br.png', style: { bottom: -size * 0.28, left: -size * 0.20 }, transform: 'scaleX(-1)' },
  }[position];
  return (
    <img
      src={config.src}
      alt=""
      aria-hidden
      className="absolute pointer-events-none select-none"
      style={{
        width: size * 1.6,
        height: 'auto',
        transform: config.transform,
        opacity,
        zIndex: 0,
        ...config.style,
      }}
    />
  );
};

// ─── Palm frond shadow — REAL PHOTOREALISTIC PNG asset ─────────────────
export const PalmShadow = ({ position = 'top-right', size = 380, opacity = 0.45 }) => {
  const pos = {
    'top-right':    { top: -size * 0.2, right: -size * 0.25 },
    'top-left':     { top: -size * 0.2, left: -size * 0.25, transform: 'scaleX(-1)' },
    'bottom-right': { bottom: -size * 0.2, right: -size * 0.25, transform: 'scaleY(-1)' },
    'bottom-left':  { bottom: -size * 0.2, left: -size * 0.25, transform: 'scale(-1, -1)' },
  }[position];
  return (
    <img
      src="/da/palm-shadow.png"
      alt=""
      aria-hidden
      className="absolute pointer-events-none select-none"
      style={{
        width: size,
        height: 'auto',
        opacity,
        zIndex: 0,
        mixBlendMode: 'multiply',
        ...pos,
      }}
    />
  );
};

// ─── Mimosa branch — REAL PHOTOREALISTIC PNG asset ─────────────────────
export const MimosaBranch = ({ size = 220, position = 'bottom-left', opacity = 0.95, flip = false }) => {
  const pos = {
    'bottom-left':  { bottom: -size * 0.18, left: -size * 0.15 },
    'bottom-right': { bottom: -size * 0.18, right: -size * 0.15 },
    'top-left':     { top: -size * 0.15, left: -size * 0.15 },
    'top-right':    { top: -size * 0.15, right: -size * 0.15 },
  }[position];
  const rotations = {
    'bottom-left':  'rotate(0deg)',
    'bottom-right': 'rotate(0deg) scaleX(-1)',
    'top-left':     'rotate(180deg) scaleX(-1)',
    'top-right':    'rotate(180deg)',
  };
  return (
    <img
      src="/da/mimosa-branch.png"
      alt=""
      aria-hidden
      className="absolute pointer-events-none select-none"
      style={{
        width: size,
        height: 'auto',
        transform: flip ? `${rotations[position]} scaleX(-1)` : rotations[position],
        opacity,
        zIndex: 0,
        filter: 'drop-shadow(0 8px 12px rgba(11,43,36,0.10))',
        ...pos,
      }}
    />
  );
};

// ─── Decorative frame — combines awning + mimosa + palm shadow ─────────
// Wraps any container to add the full Sabril DA decoration set
export const SabrilDecorFrame = ({
  awningTL = true, awningBR = true,
  awningTR = false, awningBL = false,
  mimosa = true, mimosaRight = false,
  palm = true,
  awningSize = 130, mimosaSize = 120,
}) => (
  <>
    {palm && <PalmShadow position={mimosaRight ? 'top-left' : 'top-right'} />}
    {awningTL && <AwningCorner position="top-left" size={awningSize} />}
    {awningTR && <AwningCorner position="top-right" size={awningSize} />}
    {awningBR && <AwningCorner position="bottom-right" size={awningSize} />}
    {awningBL && <AwningCorner position="bottom-left" size={awningSize} />}
    {mimosa && <MimosaBranch size={mimosaSize} position={mimosaRight ? 'bottom-right' : 'bottom-left'} flip={mimosaRight} />}
  </>
);

// ─── Section divider — sun ornament with optional tagline ──────────────
export const SabrilDivider = ({ tagline, size = 22, spacing = 22 }) => (
  <div className="flex flex-col items-center" style={{ paddingTop: spacing, paddingBottom: spacing }}>
    <div className="flex items-center gap-3 w-full max-w-[280px] mx-auto">
      <span style={{ flex: 1, height: 0.5, background: DA.goldSoft }} />
      <SunOrnament size={size} color={DA.sunBright} />
      <span style={{ flex: 1, height: 0.5, background: DA.goldSoft }} />
    </div>
    {tagline && (
      <p className="mt-3" style={{
        fontFamily: DA.sans,
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '0.32em',
        textTransform: 'uppercase',
        color: DA.goldMain,
        textAlign: 'center',
      }}>
        {tagline}
      </p>
    )}
  </div>
);
export const BrandMark = ({ size = 'md', center = true, showSun = true }) => {
  const sizes = {
    sm: { title: 22, kicker: 8, gap: 12, sunSize: 8, sunTop: -2 },
    md: { title: 32, kicker: 9.5, gap: 16, sunSize: 11, sunTop: -4 },
    lg: { title: 46, kicker: 11, gap: 22, sunSize: 15, sunTop: -6 },
  }[size];
  return (
    <div className={center ? 'text-center' : ''}>
      <p style={{
        position: 'relative',
        display: 'inline-block',
        fontFamily: DA.serif,
        fontSize: sizes.title,
        fontWeight: 700,
        color: DA.greenDeep,
        letterSpacing: '-0.02em',
        lineHeight: 1,
      }}>
        Sabr
        <span style={{ position: 'relative', display: 'inline-block' }}>
          {showSun ? 'ı' : 'i'}
          {showSun && (
            <span
              aria-hidden
              style={{
                position: 'absolute',
                left: '50%',
                top: sizes.sunTop,
                transform: 'translateX(-50%)',
                lineHeight: 0,
                pointerEvents: 'none',
              }}
            >
              <SunOrnament size={sizes.sunSize} color={DA.sunBright} />
            </span>
          )}
        </span>
        l<span style={{ color: DA.goldMain }}>.</span>
      </p>
      <div className={`flex items-center ${center ? 'justify-center' : ''} mt-1.5`} style={{ gap: 10 }}>
        <span style={{ display: 'inline-block', width: sizes.gap, height: 0.5, background: DA.goldMain }} />
        <span style={{
          fontFamily: DA.sans,
          fontSize: sizes.kicker,
          letterSpacing: '0.32em',
          color: DA.goldMain,
          fontWeight: 700,
          textTransform: 'uppercase',
        }}>
          Audit resto.
        </span>
        <span style={{ display: 'inline-block', width: sizes.gap, height: 0.5, background: DA.goldMain }} />
      </div>
    </div>
  );
};

// ─── S. Stamp (dark green circle with gold "S." — brand seal) ──────────
export const SabrilStamp = ({ size = 56 }) => (
  <span
    className="inline-flex items-center justify-center"
    style={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: DA.greenDeep,
      boxShadow: `0 6px 16px -4px ${DA.greenDeep}66, inset 0 1px 0 rgba(216,148,64,0.15)`,
      border: `1px solid rgba(216,148,64,0.25)`,
    }}
    aria-hidden
  >
    <span style={{
      fontFamily: DA.serif,
      fontSize: size * 0.5,
      fontWeight: 700,
      color: DA.goldMain,
      letterSpacing: '-0.03em',
      lineHeight: 1,
    }}>
      S<span style={{ color: DA.goldMain, marginLeft: -2 }}>.</span>
    </span>
  </span>
);

// ─── Sabril cursive signature (used in footers) ────────────────────────
export const SabrilSignature = ({ size = 34, color = DA.greenDeep }) => (
  <span
    style={{
      fontFamily: '"Great Vibes", "Dancing Script", "Snell Roundhand", cursive',
      fontSize: size,
      color,
      lineHeight: 1,
      letterSpacing: '0.01em',
      display: 'inline-block',
    }}
  >
    Sabril<span style={{ color: DA.goldMain }}>.</span>
  </span>
);

// ─── Footer signature ──────────────────────────────────────────────────
export const BrandFooter = () => (
  <div className="flex flex-col items-center">
    <SunOrnament size={22} opacity={0.9} color={DA.sunBright} />
    <div className="mt-3">
      <SabrilSignature size={36} />
    </div>
    <p className="mt-3 text-center" style={{
      fontFamily: DA.sans,
      fontSize: 9.5,
      letterSpacing: '0.28em',
      color: DA.goldMain,
      fontWeight: 700,
      textTransform: 'uppercase',
      lineHeight: 1.8,
    }}>
      Créer. Optimiser. Développer.<br />
      Des restaurants qui <span style={{ fontWeight: 800 }}>marquent</span>.
    </p>
  </div>
);

export default DA;
